import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Autocomplete,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  AutoMode as AutoModeIcon,
  Inventory as InventoryIcon,
  Storefront as StorefrontIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Image as ImageIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import GuideButton from '../../common/guide/GuideButton';

// Pazaryeri Ürün Tipi
interface MarketplaceProduct {
  id: string;
  title: string;
  price: number;
  currency: string;
  stockQuantity: number;
  categoryName: string;
  brand: string;
  barcode?: string;
  sku?: string;
  imageUrl?: string;
  status: 'active' | 'inactive' | 'out_of_stock';
  lastUpdated: string;
  // N11 özel alanları
  n11ProductId?: number;
  sellerId?: number;
  sellerNickname?: string;
}

// Mevcut Stok Ürün Tipi
interface InventoryProduct {
  id: string;
  name: string;
  barcode?: string;
  stockCode: string;
  quantity: number;
  unit: string;
  salePrice: number;
  saleCurrency?: string;
  categoryId?: string;
  brandId?: string;
  warehouseId?: string;
  isActive: boolean;
  imageUrl?: string;
  isMainItem?: boolean;
  parentItemId?: string;
}

// Depo Tipi
interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

// Eşleştirme Tipi
interface ProductMapping {
  marketplaceProduct: MarketplaceProduct;
  inventoryProduct?: InventoryProduct;
  mappingType: 'new' | 'existing' | 'manual' | 'skip';
  selectedWarehouse?: string;
  importImages: boolean;
  matchMethod?: 'barcode' | 'sku' | 'exact_name' | 'word_match' | 'similarity' | 'manual';
}

interface ProductMappingModalProps {
  open: boolean;
  onClose: () => void;
  marketplaceProducts: MarketplaceProduct[];
  inventoryProducts: InventoryProduct[];
  warehouses: Warehouse[];
  platform: string;
  companyId: string | null;
  onImport: (mappings: ProductMapping[]) => Promise<void>;
}

const ProductMappingModal: React.FC<ProductMappingModalProps> = ({
  open,
  onClose,
  marketplaceProducts,
  inventoryProducts,
  warehouses,
  platform,
  companyId,
  onImport
}) => {
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [selectedWarehouseFilter, setSelectedWarehouseFilter] = useState<Warehouse | null>(null);
  const [selectedMapping, setSelectedMapping] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [autoMatchEnabled, setAutoMatchEnabled] = useState(true);
  const [matchingCriteria, setMatchingCriteria] = useState<'all' | 'name' | 'sku' | 'barcode'>('all');
  const [selectedGlobalWarehouse, setSelectedGlobalWarehouse] = useState<string>(() => 
    warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id || ''
  );
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, percentage: 0 });

  // Platform bilgilerini al
  const getPlatformInfo = (platform: string) => {
    switch (platform) {
      case 'n11':
        return { name: 'N11', color: '#ff6600' };
      case 'hepsiburada':
        return { name: 'Hepsiburada', color: '#ff6000' };
      case 'trendyol':
        return { name: 'Trendyol', color: '#f27a1a' };
      default:
        return { name: platform, color: '#1976d2' };
    }
  };

  const platformInfo = getPlatformInfo(platform);

  // Modal açıldığında veya eşleştirme kriteri değiştiğinde eşleştirmeleri başlat
  useEffect(() => {
    if (open && marketplaceProducts.length > 0) {
      initializeMappings();
    }
  }, [open, marketplaceProducts, matchingCriteria]);

  // Eşleştirmeleri başlat
  const initializeMappings = () => {
    const initialMappings: ProductMapping[] = marketplaceProducts.map(mpProduct => {
      let matchedInventoryProduct: InventoryProduct | undefined;
      let matchMethod: string = '';
      
      if (autoMatchEnabled) {
        // Otomatik eşleştirme: Barkod, SKU veya isim benzerliği
        const matchResult = findBestMatch(mpProduct);
        matchedInventoryProduct = matchResult.product;
        matchMethod = matchResult.method;
      }

      return {
        marketplaceProduct: mpProduct,
        inventoryProduct: matchedInventoryProduct,
        mappingType: matchedInventoryProduct ? 'existing' : 'new',
        selectedWarehouse: selectedGlobalWarehouse,
        importImages: true,
        matchMethod: matchMethod as any
      };
    });

    setMappings(initialMappings);
  };

  // En iyi eşleşmeyi bul ve eşleştirme yöntemini döndür
  const findBestMatch = (mpProduct: MarketplaceProduct): { product: InventoryProduct | undefined, method: string } => {
    // Seçilen kritere göre eşleştirme yap
    switch (matchingCriteria) {
      case 'barcode':
        // Sadece barkod eşleşmesi
        if (mpProduct.barcode) {
          const barcodeMatch = inventoryProducts.find(ip => 
            ip.barcode && ip.barcode.toLowerCase().trim() === mpProduct.barcode!.toLowerCase().trim()
          );
          if (barcodeMatch) return { product: barcodeMatch, method: 'barcode' };
        }
        break;

      case 'sku':
        // Sadece SKU eşleşmesi
        if (mpProduct.sku) {
          const skuMatch = inventoryProducts.find(ip => 
            ip.stockCode && ip.stockCode.toLowerCase().trim() === mpProduct.sku!.toLowerCase().trim()
          );
          if (skuMatch) return { product: skuMatch, method: 'sku' };
        }
        break;

      case 'name':
        // Sadece tam ürün adı eşleşmesi (birebir aynı olmalı)
        const nameOnlyMatch = inventoryProducts.find(ip => 
          ip.name.toLowerCase().trim() === mpProduct.title.toLowerCase().trim()
        );
        if (nameOnlyMatch) return { product: nameOnlyMatch, method: 'exact_name' };
        break;

      case 'all':
      default:
        // Tüm kriterler (öncelik sırasına göre)
        
        // 1. Barkod eşleşmesi (en yüksek öncelik)
        if (mpProduct.barcode) {
          const barcodeMatch = inventoryProducts.find(ip => 
            ip.barcode && ip.barcode.toLowerCase().trim() === mpProduct.barcode!.toLowerCase().trim()
          );
          if (barcodeMatch) return { product: barcodeMatch, method: 'barcode' };
        }

        // 2. SKU eşleşmesi
        if (mpProduct.sku) {
          const skuMatch = inventoryProducts.find(ip => 
            ip.stockCode && ip.stockCode.toLowerCase().trim() === mpProduct.sku!.toLowerCase().trim()
          );
          if (skuMatch) return { product: skuMatch, method: 'sku' };
        }

        // 3. Tam ürün adı eşleşmesi (birebir aynı olmalı)
        const allCriteriaNameMatch = inventoryProducts.find(ip => 
          ip.name.toLowerCase().trim() === mpProduct.title.toLowerCase().trim()
        );
        if (allCriteriaNameMatch) return { product: allCriteriaNameMatch, method: 'exact_name' };
        break;
    }

    return { product: undefined, method: '' };
  };

  // İsim benzerliği hesapla (basit Levenshtein distance)
  const calculateSimilarity = (str1: string, str2: string): number => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  };

  // Levenshtein distance algoritması
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Eşleştirme güncelle
  const updateMapping = (index: number, updates: Partial<ProductMapping>) => {
    setMappings(prev => prev.map((mapping, i) => 
      i === index ? { ...mapping, ...updates } : mapping
    ));
  };

  // Mevcut ürünle eşleştir
  const linkToExistingProduct = (mappingIndex: number, inventoryProduct: InventoryProduct) => {
    const currentMapping = mappings[mappingIndex];
    updateMapping(mappingIndex, {
      inventoryProduct,
      mappingType: currentMapping.mappingType === 'manual' ? 'manual' : 'existing',
      matchMethod: 'manual'
    });
  };

  // Eşleştirmeyi kaldır
  const unlinkProduct = (mappingIndex: number) => {
    updateMapping(mappingIndex, {
      inventoryProduct: undefined,
      mappingType: 'new'
    });
  };

  // Filtrelenmiş pazaryeri ürünleri
  const filteredMarketplaceProducts = marketplaceProducts.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.barcode && product.barcode.includes(searchTerm)) ||
    (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filtrelenmiş stok ürünleri
  const filteredInventoryProducts = inventoryProducts.filter(product => {
    // Arama filtresi
    const matchesSearch = product.name.toLowerCase().includes(inventorySearchTerm.toLowerCase()) ||
      (product.barcode && product.barcode.includes(inventorySearchTerm)) ||
      product.stockCode.toLowerCase().includes(inventorySearchTerm.toLowerCase());
    
    // Depo filtresi
    const matchesWarehouse = selectedWarehouseFilter ? product.warehouseId === selectedWarehouseFilter.id : true;
    
    return matchesSearch && matchesWarehouse;
  });

  // Eşleştirme yöntemini Türkçe'ye çevir
  const getMatchMethodText = (method?: string): string => {
    switch (method) {
      case 'barcode': return '🏷️ Barkod';
      case 'sku': return '📦 Stok Kodu';
      case 'exact_name': return '📝 Exact Name';
      case 'word_match': return '🔤 Word Match';
      case 'similarity': return '📊 Benzerlik';
      case 'manual': return '✋ Manually Selected';
      default: return '';
    }
  };

  // İçe aktarma işlemi
  const handleImport = async () => {
    const validMappings = mappings.filter(mapping => mapping.mappingType !== 'skip');
    
    if (validMappings.length === 0) {
      return;
    }

    // Tüm ürünlerin depo seçimini global depo ile güncelle
    const updatedMappings = validMappings.map(mapping => ({
      ...mapping,
      selectedWarehouse: selectedGlobalWarehouse
    }));

    setImporting(true);
    setShowProgressModal(true);
    setImportProgress({ current: 0, total: updatedMappings.length, percentage: 0 });

    try {
      // Progress callback ile onImport çağır
      await onImportWithProgress(updatedMappings);
      
      // Başarılı tamamlandığında modalları kapat
      setTimeout(() => {
        setShowProgressModal(false);
        onClose();
      }, 1500); // 1.5 saniye sonra kapat
      
    } catch (error) {
      console.error('Import error:', error);
      setShowProgressModal(false);
    } finally {
      setImporting(false);
    }
  };

  // Progress ile içe aktarma
  const onImportWithProgress = async (mappings: ProductMapping[]) => {
    const total = mappings.length;
    
    for (let i = 0; i < mappings.length; i++) {
      // Her ürün için progress güncelle
      const current = i + 1;
      const percentage = Math.round((current / total) * 100);
      
      setImportProgress({ current, total, percentage });
      
      // Tek ürün için import işlemi (simüle edilmiş)
      await new Promise(resolve => setTimeout(resolve, 500)); // Her ürün için 500ms gecikme
    }
    
    // Gerçek import işlemini çağır
    await onImport(mappings);
  };

  // İstatistikler
  const stats = {
    total: mappings.length,
    new: mappings.filter(m => m.mappingType === 'new').length,
    existing: mappings.filter(m => m.mappingType === 'existing').length,
    manual: mappings.filter(m => m.mappingType === 'manual').length,
    skipped: mappings.filter(m => m.mappingType === 'skip').length
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '90vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Ürün Eşleştirme - {platformInfo.name}
            </Typography>
            <GuideButton guideKey="ecommerce.productMapping" size="small" sx={{ ml: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoMatchEnabled}
                  onChange={(e) => setAutoMatchEnabled(e.target.checked)}
                />
              }
              label="Auto Matching"
            />
            <Chip 
              label={`${stats.total} Products`}
              color="primary"
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>
      
      {/* Sabit Üst Alan - İstatistik Kartları */}
      <Box sx={{ 
        px: 3, 
        pb: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 1
      }}>
        <Grid container spacing={1}>
          <Grid item xs={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h6" color="primary">{stats.new}</Typography>
                <Typography variant="caption">Yeni Ürün</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h6" color="success.main">{stats.existing}</Typography>
                <Typography variant="caption">Otomatik</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h6" color="info.main">{stats.manual}</Typography>
                <Typography variant="caption">Elle Seçildi</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h6" color="warning.main">{stats.skipped}</Typography>
                <Typography variant="caption">Atlandı</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={2.4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h6">{stats.total}</Typography>
                <Typography variant="caption">Toplam</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
        <Grid container spacing={0} sx={{ height: '100%' }}>
          {/* Sol Panel - Pazaryeri Ürünleri */}
          <Grid item xs={5} sx={{ height: '70vh', borderRight: 1, borderColor: 'divider' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Sabit Başlık ve Arama */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StorefrontIcon sx={{ mr: 1, color: platformInfo.color }} />
                  <Typography variant="h6">{platformInfo.name} Ürünleri</Typography>
                </Box>
                {/* Arama ve Eşleştirme Kriteri */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search in marketplace products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Eşleştirme Kriteri</InputLabel>
                    <Select
                      value={matchingCriteria}
                      onChange={(e) => setMatchingCriteria(e.target.value as any)}
                      label="Matching Criteria"
                    >
                      <MenuItem value="all">Tüm Kriterler</MenuItem>
                      <MenuItem value="name">Sadece Ürün Adı</MenuItem>
                      <MenuItem value="sku">Sadece Stok Kodu</MenuItem>
                      <MenuItem value="barcode">Sadece Barkod</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              {/* Kaydırılabilir İçerik */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}>
                <List dense>
                  {filteredMarketplaceProducts.map((product, index) => {
                    const mapping = mappings.find(m => m.marketplaceProduct.id === product.id);
                    const mappingIndex = mappings.findIndex(m => m.marketplaceProduct.id === product.id);
                    
                    return (
                      <ListItem 
                        key={product.id}
                        sx={{ 
                          border: 1, 
                          borderColor: 'divider', 
                          mb: 1, 
                          borderRadius: 1,
                          bgcolor: mapping?.mappingType === 'existing' ? 'success.light' : 
                                   mapping?.mappingType === 'skip' ? 'grey.100' : 'background.paper'
                        }}
                      >
                        <ListItemAvatar>
                          {product.imageUrl ? (
                            <Avatar src={product.imageUrl} variant="rounded" />
                          ) : (
                            <Avatar variant="rounded">
                              <ImageIcon />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {product.title}
                              </Typography>
                              {mapping?.inventoryProduct && (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Fiyat: {product.price} {product.currency} | Stok: {product.stockQuantity}
                              </Typography>
                              {product.barcode && (
                                <Typography variant="caption" display="block">
                                  Barkod: {product.barcode}
                                </Typography>
                              )}
                              {product.sku && (
                                <Typography variant="caption" display="block">
                                  SKU: {product.sku}
                                </Typography>
                              )}
                              {mapping && (
                                <Box sx={{ mt: 1 }}>
                                  {/* Eşleştirilen ürün bilgisi */}
                                  {(mapping.mappingType === 'existing' || mapping.mappingType === 'manual') && mapping.inventoryProduct && (
                                    <Box sx={{ mb: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                        🔗 Eşleştirilen Ürün:
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'success.dark' }}>
                                        {mapping.inventoryProduct.name}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: 'success.dark' }}>
                                        SKU: {mapping.inventoryProduct.stockCode}
                                      </Typography>
                                      {mapping.matchMethod && (
                                        <Typography variant="caption" display="block" sx={{ color: 'success.dark', fontWeight: 'bold' }}>
                                          {getMatchMethodText(mapping.matchMethod)}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                  
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>İşlem</InputLabel>
                                      <Select
                                        value={mapping.mappingType}
                                        onChange={(e) => {
                                          const newType = e.target.value as any;
                                          if (newType === 'manual') {
                                            // Elle seçim moduna geç
                                            updateMapping(mappingIndex, { 
                                              mappingType: 'manual',
                                              inventoryProduct: undefined,
                                              matchMethod: 'manual'
                                            });
                                            setSelectedMapping(product.id);
                                          } else {
                                            updateMapping(mappingIndex, { mappingType: newType });
                                          }
                                        }}
                                        label="Action"
                                      >
                                        <MenuItem value="new">Yeni Ürün</MenuItem>
                                        <MenuItem value="existing">Otomatik Eşleştir</MenuItem>
                                        <MenuItem value="manual">Elle Seç</MenuItem>
                                        <MenuItem value="skip">Atla</MenuItem>
                                      </Select>
                                    </FormControl>
                                    
                                    {mapping.mappingType !== 'skip' && (
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={mapping.importImages}
                                            onChange={(e) => updateMapping(mappingIndex, { importImages: e.target.checked })}
                                          />
                                        }
                                        label="Resim"
                                      />
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Grid>

          {/* Orta Panel - Eşleştirme Alanı */}
          <Grid item xs={2} sx={{ height: '70vh', borderRight: 1, borderColor: 'divider' }}>
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2
            }}>
              {/* Üst Kısım - Eşleştirme */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <ArrowForwardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h6" textAlign="center">
                  Eşleştirme
                </Typography>
                
                {selectedMapping ? (
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
                      ✋ Elle Seçim Modu
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ color: 'info.dark' }}>
                      Sağdaki listeden bir ürün seçin
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedMapping(null)}
                      sx={{ mt: 1 }}
                    >
                      İptal
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<AutoModeIcon />}
                      onClick={initializeMappings}
                      disabled={importing}
                    >
                      Otomatik Eşleştir
                    </Button>
                    <Typography variant="caption" textAlign="center" sx={{ mt: 1, color: 'text.secondary' }}>
                      Criteria: {matchingCriteria === 'all' ? 'All Criteria' :
                               matchingCriteria === 'name' ? 'Product Name' :
                               matchingCriteria === 'sku' ? 'Stok Kodu' : 'Barkod'}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Alt Kısım - Depo Seçimi */}
              <Box sx={{ 
                width: '100%', 
                textAlign: 'center', 
                p: 2, 
                bgcolor: 'warning.light', 
                borderRadius: 1,
                border: 1,
                borderColor: 'warning.main'
              }}>
                <WarehouseIcon sx={{ fontSize: 24, color: 'warning.dark', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark', mb: 1 }}>
                  İçe Aktarma Deposu
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={selectedGlobalWarehouse}
                    onChange={(e) => setSelectedGlobalWarehouse(e.target.value)}
                    displayEmpty
                  >
                    {warehouses.map(warehouse => (
                      <MenuItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" display="block" sx={{ color: 'warning.dark', mt: 1 }}>
                  Tüm ürünler bu depoya aktarılacak
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Sağ Panel - Mevcut Stok Ürünleri */}
          <Grid item xs={5} sx={{ height: '70vh' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Sabit Başlık ve Filtreler */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Mevcut Stok Ürünleri</Typography>
                </Box>
                
                {/* Arama ve Depo Filtresi */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search in stock products..."
                    value={inventorySearchTerm}
                    onChange={(e) => setInventorySearchTerm(e.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Autocomplete
                    size="small"
                    options={warehouses}
                    getOptionLabel={(option) => option.name}
                    value={selectedWarehouseFilter}
                    onChange={(_, newValue) => setSelectedWarehouseFilter(newValue)}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        placeholder="Depo Filtresi" 
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <WarehouseIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
              
              {/* Kaydırılabilir İçerik */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}>
                <List dense>
                  {filteredInventoryProducts.map((product) => {
                    // Bu ürün herhangi bir eşleştirmede kullanılıyor mu kontrol et
                    const isLinked = mappings.some(mapping => 
                      mapping.inventoryProduct?.id === product.id && (mapping.mappingType === 'existing' || mapping.mappingType === 'manual')
                    );
                    
                    return (
                      <ListItem 
                        key={product.id}
                        sx={{ 
                          border: 1, 
                          borderColor: isLinked ? 'success.main' : selectedMapping ? 'info.main' : 'divider', 
                          mb: 1, 
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: isLinked ? 'success.light' : selectedMapping ? 'info.light' : 'background.paper',
                          '&:hover': { 
                            bgcolor: isLinked ? 'success.light' : selectedMapping ? 'info.light' : 'action.hover',
                            borderColor: isLinked ? 'success.main' : selectedMapping ? 'info.main' : 'primary.main'
                          }
                        }}
                        onClick={() => {
                          if (selectedMapping) {
                            const mappingIndex = mappings.findIndex(m => m.marketplaceProduct.id === selectedMapping);
                            if (mappingIndex !== -1) {
                              linkToExistingProduct(mappingIndex, product);
                              setSelectedMapping(null);
                            }
                          }
                        }}
                      >
                        <ListItemAvatar>
                          {product.imageUrl ? (
                            <Avatar src={product.imageUrl} variant="rounded" />
                          ) : (
                            <Avatar variant="rounded">
                              <InventoryIcon />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {product.name}
                              </Typography>
                              {isLinked && (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Fiyat: {product.salePrice} {product.saleCurrency || 'TRY'} | Stok: {product.quantity} {product.unit}
                              </Typography>
                              {product.barcode && (
                                <Typography variant="caption" display="block">
                                  Barkod: {product.barcode}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block">
                                SKU: {product.stockCode}
                              </Typography>
                              {isLinked && (
                                <Typography variant="caption" display="block" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                  ✓ Eşleştirildi
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={importing}>
          İptal
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {stats.new} yeni, {stats.existing} otomatik, {stats.manual} elle, {stats.skipped} atlandı
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={importing ? <CircularProgress size={20} /> : <LinkIcon />}
            onClick={handleImport}
            disabled={importing || stats.new + stats.existing + stats.manual === 0}
          >
            {importing ? 'Importing...' : `Import ${stats.new + stats.existing + stats.manual} Products`}
          </Button>
        </Box>
      </DialogActions>

      {/* Progress Modal */}
      <Dialog
        open={showProgressModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: { 
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="h6">
              Ürünler İçe Aktarılıyor...
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  İlerleme
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importProgress.percentage}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={importProgress.percentage} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  }
                }}
              />
            </Box>

            {/* İstatistikler */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              mb: 2
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {importProgress.current}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tamamlanan
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="warning.main">
                  {importProgress.total - importProgress.current}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Kalan
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">
                  {importProgress.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Toplam
                </Typography>
              </Box>
            </Box>

            {/* Mevcut İşlem */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {importProgress.current > 0 && importProgress.current <= importProgress.total ? 
                  `${importProgress.current}/${importProgress.total} products are being processed...` :
                  'Import is starting...'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ProductMappingModal;
